import { useEffect, useState, useCallback } from "react";

/** Minimal pushState-backed location hook. Taco uses a single SPA
 *  with two top-level "pages" (Dashboard and Admin) — dragging in
 *  react-router-dom for that is gratuitous. The Go server's SPA
 *  fallback (internal/ui/embed.go) serves index.html for any path
 *  without an extension, so /admin renders the same bundle as /.
 *
 *  Calling navigate("/admin") from the Dashboard pushes a history
 *  entry (back button works) and fires a state update so React
 *  re-renders. Listens to popstate so the browser's back/forward
 *  buttons also drive the state. */
export function useLocation() {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    if (to === window.location.pathname) return;
    window.history.pushState(null, "", to);
    setPathname(to);
  }, []);

  /** Replace the current entry without leaving one in history. Used
   *  for non-admin users bounced off /admin — they shouldn't land on
   *  /admin when hitting Back. */
  const replace = useCallback((to: string) => {
    window.history.replaceState(null, "", to);
    setPathname(to);
  }, []);

  return { pathname, navigate, replace };
}
